// Rol işlevleri yardımcı modülü

// Yerel depolama anahtar sabitleri
const ROLES_STORAGE_KEY = 'iadepanel_roles';
const LAST_UPDATED_KEY = 'iadepanel_roles_updated';

// Başlangıçta bilinen rol tanımları
export const ROLES = {
  ADMIN: 'admin',
  CASI: 'casi'
};

// Yerel depolamadan rolleri al
export const getRolesFromStorage = () => {
  try {
    const rolesData = localStorage.getItem(ROLES_STORAGE_KEY);
    if (rolesData) {
      return JSON.parse(rolesData);
    }
  } catch (error) {
    console.error('Rol bilgisi alınamadı:', error);
  }
  return [];
};

// Rolleri yerel depolamaya kaydet
export const saveRolesToStorage = (roles) => {
  try {
    localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(roles));
    localStorage.setItem(LAST_UPDATED_KEY, new Date().toISOString());
  } catch (error) {
    console.error('Roller kaydedilemedi:', error);
  }
};

// Sistem rolleri haritası - SADECE admin ve casi için sabit değerler
const SYSTEM_ROLES = {
  1: { id: 1, name: 'admin', display_name: 'Yönetici' },
  2: { id: 2, name: 'casi', display_name: 'CASI Yetkili' }
};

// Tüm roller için dinamik harita - localStorage'dan yüklenecek
let ROLE_MAP = { ...SYSTEM_ROLES };

// Rol haritasını güncelle - sadece dışarıdan kullanım için
export const updateRoleMap = (roles) => {
  // Mevcut sistem rollerini koru ve yeni rolleri ekle
  const newRoleMap = { ...SYSTEM_ROLES };
  
  if (Array.isArray(roles)) {
    roles.forEach(role => {
      // Sistem rolleri dışındaki roller için
      if (!SYSTEM_ROLES[role.id]) {
        newRoleMap[role.id] = role;
      }
    });
  }
  
  ROLE_MAP = newRoleMap;
  return ROLE_MAP;
};

/**
 * Rol ID değerini rol nesnesine dönüştürür
 * 
 * @param {number|string} roleId - Rol ID değeri
 * @returns {Object|null} - Rol nesnesi veya bulunamazsa null
 */
export const resolveRole = (roleId) => {
  // roleId sayı değilse sayıya dönüştür
  const id = typeof roleId === 'string' ? parseInt(roleId, 10) : roleId;
  
  // Önce sistem rol haritasında ara
  if (SYSTEM_ROLES[id]) {
    return SYSTEM_ROLES[id];
  }
  
  // Sistem rollerinde yoksa, dinamik rol haritasında ara
  if (ROLE_MAP[id]) {
    return ROLE_MAP[id];
  }
  
  // Hala bulunamadıysa, localStorage'dan rolleri al ve tekrar dene
  try {
    const storedRoles = getRolesFromStorage();
    const foundRole = storedRoles.find(r => r.id === id);
    
    if (foundRole) {
      // Bulunan rolü haritaya ekle ve döndür
      ROLE_MAP[id] = foundRole;
      return foundRole;
    }
  } catch (error) {
    console.error('Rol çözümleme hatası:', error);
  }
  
  // Bulunamazsa hata raporu ve null
  console.warn(`${id} ID'li rol bulunamadı`);
  return null;
};

/**
 * Rol adını rol ID'sine dönüştürür
 * 
 * @param {string} roleName - Rol adı
 * @returns {number|null} - Rol ID'si veya bulunamazsa null
 */
export const getRoleIdByName = (roleName) => {
  // Önce sistem rollerinde ara
  for (const [id, role] of Object.entries(SYSTEM_ROLES)) {
    if (role.name === roleName) {
      return parseInt(id, 10);
    }
  }
  
  // Sistem rollerinde yoksa, dinamik rol haritasında ara
  for (const [id, role] of Object.entries(ROLE_MAP)) {
    if (role.name === roleName) {
      return parseInt(id, 10);
    }
  }
  
  // Hala bulunamadıysa, localStorage'dan rolleri al ve tekrar dene
  try {
    const storedRoles = getRolesFromStorage();
    const foundRole = storedRoles.find(r => r.name === roleName);
    
    if (foundRole) {
      return foundRole.id;
    }
  } catch (error) {
    console.error('Rol ID bulma hatası:', error);
  }
  
  console.warn(`'${roleName}' adlı rol bulunamadı`);
  return null;
};

/**
 * Rol adını görünen rol adına dönüştürür
 * 
 * @param {string} roleName - Rol adı
 * @returns {string} - Görünen rol adı veya bulunamazsa rol adının kendisi
 */
export const getRoleDisplayName = (roleName) => {
  // Önce sistem rollerinde ara
  for (const role of Object.values(SYSTEM_ROLES)) {
    if (role.name === roleName) {
      return role.display_name;
    }
  }
  
  // Sistem rollerinde yoksa, dinamik rol haritasında ara
  for (const role of Object.values(ROLE_MAP)) {
    if (role.name === roleName) {
      return role.display_name;
    }
  }
  
  // Hala bulunamadıysa, localStorage'dan rolleri al ve tekrar dene
  try {
    const storedRoles = getRolesFromStorage();
    const foundRole = storedRoles.find(r => r.name === roleName);
    
    if (foundRole) {
      return foundRole.display_name;
    }
  } catch (error) {
    console.error('Rol görünen adı bulma hatası:', error);
  }
  
  return roleName; // Bulunamazsa rol adının kendisini döndür
};

// Rollerin güncellenmesi gerekip gerekmediğini kontrol et
export const shouldRefreshRoles = () => {
  try {
    const lastUpdated = localStorage.getItem(LAST_UPDATED_KEY);
    
    if (!lastUpdated) return true;
    
    const lastUpdateDate = new Date(lastUpdated);
    const now = new Date();
    
    // Son güncellemeden beri 1 saat geçtiyse güncelleme zamanı
    const hourInMillis = 60 * 60 * 1000;
    return now - lastUpdateDate > hourInMillis;
  } catch (error) {
    return true;
  }
};

// Rolleri API'den al ve başlat
export const initializeRoles = async () => {
  try {
    // Eğer roller zaten var ve güncelleme zamanı gelmediyse, mevcut rolleri kullan
    const existingRoles = getRolesFromStorage();
    if (existingRoles.length > 0 && !shouldRefreshRoles()) {
      // Rol haritasını güncelle
      updateRoleMap(existingRoles);
      return existingRoles;
    }
    
    // API'den rolleri al
    const response = await fetch('/api/roles/setup');
    
    if (!response.ok) {
      throw new Error(`API yanıt hatası: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success && Array.isArray(data.data)) {
      // Rolleri yerel depolamaya kaydet
      saveRolesToStorage(data.data);
      // Rol haritasını güncelle
      updateRoleMap(data.data);
      return data.data;
    } else {
      throw new Error('Rol verisi doğru formatta değil');
    }
  } catch (error) {
    console.error('Roller yüklenirken hata:', error);
    
    // Hata durumunda, mevcut rolleri döndür (eğer varsa)
    const existingRoles = getRolesFromStorage();
    if (existingRoles.length > 0) {
      // Rol haritasını güncelle
      updateRoleMap(existingRoles);
      return existingRoles;
    }
    
    // Hiç rol yoksa, varsayılan bir rol kümesi döndür
    const defaultRoles = [
      { id: 1, name: 'admin', display_name: 'Admin', is_system_role: 1 },
      { id: 2, name: 'casi', display_name: 'CASI Yetkili', is_system_role: 1 }
    ];
    
    saveRolesToStorage(defaultRoles);
    // Rol haritasını güncelle
    updateRoleMap(defaultRoles);
    return defaultRoles;
  }
};

// Kullanıcı rol bilgilerine göre sayfa yetkisi olup olmadığını sorgula
export const hasPagePermission = (user, requiredRoles = []) => {
  // Kullanıcı yoksa, yetki yoktur
  if (!user) return false;
  
  // Admin her zaman erişebilir
  if (user.role === 'admin' || user.role_id === 1) return true;
  
  // Rol gerekmiyor veya rollerden biri varsa yetkilidir
  if (requiredRoles.length === 0) return true;
  
  // Kullanıcının rolünü al
  const userRole = user.role || (user.role_id ? resolveRole(user.role_id)?.name : null);
  
  return userRole && requiredRoles.includes(userRole);
};

// Rol renk sınıfları
export const getRoleColorClass = (roleId) => {
  if (!roleId) return 'bg-gray-100 text-gray-800';
  
  // Kullanıcı nesnesini geçirmişsek, içinden role_id değerini al
  if (typeof roleId === 'object') {
    // Eğer kullanıcı nesnesinde role_name varsa, değerlendir
    if (roleId.role_name) {
      const roleName = roleId.role_name.toLowerCase();
      
      switch (roleName) {
        case ROLES.ADMIN:
          return 'bg-red-100 text-red-800';
        case ROLES.CASI:
          return 'bg-blue-100 text-blue-800';
        default:
          return 'bg-purple-100 text-purple-800';
      }
    }
    
    roleId = roleId.role_id;
    
    if (!roleId) {
      return 'bg-gray-100 text-gray-800';
    }
  }
  
  const role = resolveRole(roleId);
  
  if (role) {
    const roleName = role.name.toLowerCase();
    
    switch (roleName) {
      case ROLES.ADMIN:
        return 'bg-red-100 text-red-800';
      case ROLES.CASI:
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-purple-100 text-purple-800';
    }
  }
  
  // Eğer rol önbellekte yoksa, ID'ye göre tahmin et
  switch (parseInt(roleId, 10)) {
    case 1:
      return 'bg-red-100 text-red-800'; // Admin
    case 2:
      return 'bg-blue-100 text-blue-800'; // CASI
    default:
      return 'bg-purple-100 text-purple-800';
  }
};